import _ from 'lodash';
import { get, post, put, destroy } from './APIClient';

module.exports = {
  createSurvey,
  getSurveys,
  getSurvey,
  updateSurvey,
  deleteSurvey,
  getResponses,
  getResponse,
  getAnalytics,
  getReport,
  generateReport,
  generateInsights,
  getPreviewReply,
  getPublicSurvey,
  startPublicResponse,
  getPublicResponse,
  sendPublicMessage,
};

// the API client resolves failures as { error }, so every call here turns that into a thrown Error
async function unwrap(request) {
  const res = await request;
  if(res.error) {
    throw new Error(res.error);
  }
  return _.get(res, 'data');
}

// mode is chat, nps or feedback; goal drives the chat, question is the fixed modes' one question
function createSurvey({
  workspaceId, name, goal, mode, question,
}) {
  return unwrap(post(`/workspaces/${workspaceId}/surveys`, {
    name, goal, mode, question,
  }));
}

// { surveys, limitReached }
function getSurveys({ workspaceId }) {
  return unwrap(get(`/workspaces/${workspaceId}/surveys`));
}

function getSurvey({ workspaceId, surveyId }) {
  return unwrap(get(`/workspaces/${workspaceId}/surveys/${surveyId}`));
}

// only the editable fields are sent; the API rejects anything else
function updateSurvey({ workspaceId, surveyId, survey }) {
  return unwrap(put(
    `/workspaces/${workspaceId}/surveys/${surveyId}`,
    _.pick(survey, ['name', 'title', 'description', 'isPublic', 'design', 'settings', 'fixed', 'launcher']),
  ));
}

function deleteSurvey({ workspaceId, surveyId }) {
  return unwrap(destroy(`/workspaces/${workspaceId}/surveys/${surveyId}`));
}

// { responses, total, page, limit, totalPages, hasMore }
function getResponses({
  workspaceId, surveyId, page, limit, completed, sentiment, date,
}) {
  const params = { page, limit, sentiment, date };
  if(completed !== undefined) {
    params.completed = completed;
  }
  return unwrap(get(`/workspaces/${workspaceId}/surveys/${surveyId}/responses`, { params }));
}

function getResponse({ workspaceId, surveyId, responseId }) {
  return unwrap(get(`/workspaces/${workspaceId}/surveys/${surveyId}/responses/${responseId}`));
}

// { stats, insights }
function getAnalytics({ workspaceId, surveyId }) {
  return unwrap(get(`/workspaces/${workspaceId}/surveys/${surveyId}/analytics`));
}

function getReport({ workspaceId, surveyId }) {
  return unwrap(get(`/workspaces/${workspaceId}/surveys/${surveyId}/report`));
}

function generateReport({ workspaceId, surveyId }) {
  return unwrap(post(`/workspaces/${workspaceId}/surveys/${surveyId}/report`));
}

// { insights }
function generateInsights({ workspaceId, surveyId }) {
  return unwrap(post(`/workspaces/${workspaceId}/surveys/${surveyId}/insights`));
}

// editor preview: { content, shouldComplete } for the conversation so far, nothing is stored
// sends the editor's unsaved language and question limit, so the preview answers the way the survey on screen would
function getPreviewReply({ workspaceId, survey, conversation }) {
  const settings = {
    language: survey.settings?.language || undefined,
    maxQuestions: survey.settings?.maxQuestions || undefined,
  };
  return unwrap(post(`/workspaces/${workspaceId}/surveys/${survey._id}/preview`, { conversation, settings }));
}

function getPublicSurvey({ publicId }) {
  return unwrap(get(`/surveys/${publicId}`, { withCredentials: false }));
}

// { _id, token, conversation, isComplete }; the token is what lets the respondent continue later.
// nps and feedback surveys send the score and comment here and are usually complete straight away.
// `inviteId` ties the response back to the email campaign contact who was sent the link
function startPublicResponse({
  publicId, score, comment, page, inviteId,
}) {
  return unwrap(post(`/surveys/${publicId}/responses`, {
    score, comment, page, inviteId,
  }, { withCredentials: false }));
}

function getPublicResponse({ responseId, token }) {
  return unwrap(get(`/surveys/responses/${responseId}`, { params: { token }, withCredentials: false }));
}

function sendPublicMessage({ responseId, token, content }) {
  return unwrap(post(`/surveys/responses/${responseId}/messages`, { token, content }, { withCredentials: false }));
}
